import { useState } from 'react';
import { supabase } from '../../../lib/supabaseClient';

interface ReviewFormProps {
  jobId: string;
  providerId: string;
  providerName: string;
  onReviewSubmitted: () => void;
}

export default function ReviewForm({ jobId, providerId, providerName, onReviewSubmitted }: ReviewFormProps) {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (rating === 0) {
      alert('Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/submit-review`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({
            jobId,
            providerId,
            rating,
            comment: comment.trim(),
            photos: []
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit review');
      }

      alert('Thank you for your review!');
      onReviewSubmitted();
    } catch (error: any) {
      console.error('Error submitting review:', error);
      alert(error.message || 'Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
        <i className="ri-star-line text-yellow-500"></i>
        Rate Your Experience
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-3">
            How was your experience with {providerName}?
          </p>
          <div className="flex gap-2 justify-center py-4">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                className="w-16 h-16 flex items-center justify-center transition-transform hover:scale-110 cursor-pointer"
              >
                <i
                  className={`text-5xl ${
                    star <= (hoveredRating || rating)
                      ? 'ri-star-fill text-yellow-500'
                      : 'ri-star-line text-gray-300'
                  }`}
                ></i>
              </button>
            ))}
          </div>
          {rating > 0 && (
            <p className="text-center text-gray-600 font-medium">
              {rating === 1 && 'Poor'}
              {rating === 2 && 'Fair'}
              {rating === 3 && 'Good'}
              {rating === 4 && 'Very Good'}
              {rating === 5 && 'Excellent'}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Share your experience (Optional)
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Tell us about your experience..."
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none resize-none text-sm"
            rows={4}
            maxLength={500}
          />
          <p className="text-xs text-gray-500 mt-1">{comment.length}/500 characters</p>
        </div>

        <button
          type="submit"
          disabled={submitting || rating === 0}
          className="w-full py-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl hover:from-teal-700 hover:to-teal-800 transition-all duration-300 font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              Submitting...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-send-plane-fill"></i>
              Submit Review
            </span>
          )}
        </button>
      </form>
    </div>
  );
}
